import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import JSZip from 'jszip';

// Components
import ModalContainer from '../../containers/ModalContainer';
import { Loader } from '../../../inputs/Loader';
import { CloudinaryQualitySelector } from '../../../inputs/CloudinaryQualitySelector';

// Styled Components
import { Container, Icon, Title, Button, ProgressText } from './styles';

// Types
import { ModalProps } from './types';

// Selectors
import { selectImagesByMultipleOrderIDs } from '../../../../gql/selectors/multimedia';

// Utils
import { getExtensionFromBlob } from '../../../../utils/imageExtension';
import { CloudinaryQuality } from '../../../../utils/cloudinary';
import { fetchDownloadBlob } from '../../../../utils/imageDownload';

// Assets
import DownloadIcon from '../../../../assets/icons/download-icon.svg';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../../../../gql/client';
import { FETCH_ORDERS } from '../../../../gql/queries/orders/orders';
import { saveBlob } from '../../../../utils/saveBlob';

const FETCH_ALL_TAKE = 99999;

/**
 * Resuelve el conjunto final de orderIDs a procesar según el modo de selección.
 *
 * - Modo individual (`selectAll === false` / undefined): devuelve `data.selectedRows`
 *   tal cual, sin tocar la red.
 * - Modo "seleccionar todo" (`selectAll === true`): vuelve a ejecutar `FETCH_ORDERS`
 *   con los mismos filtros que la tabla, take=99999 y `fetchPolicy: 'no-cache'`
 *   (ver lección de `DownloadHistoricModal`: `network-only` corrompe la caché de
 *   Apollo con takes grandes). Luego filtra los IDs presentes en `excludedIds`
 *   para respetar las exclusiones que el usuario hizo en la UI.
 *
 * El selector posterior (`selectImagesByMultipleOrderIDs`) ya tolera
 * `Array<string | undefined>`, así que devolvemos el shape original.
 */
async function resolveOrderIds(data: {
  selectAll?: boolean;
  selectedRows?: Array<string | undefined>;
  excludedIds?: string[];
  filters?: Record<string, any>;
}): Promise<Array<string | undefined>> {
  if (!data.selectAll) {
    return data.selectedRows ?? [];
  }
  // Defense in depth: even if the caller forgot to strip them, drop `id`
  // (the tableId), `take`, and `cursor` — none are valid FilterOrderList fields.
  const { id: _id, take: _take, cursor: _cursor, ...filterFields } = data.filters || {};
  const result = await client.query({
    query: FETCH_ORDERS,
    variables: {
      input: {
        ...filterFields,
        take: FETCH_ALL_TAKE,
        cursor: '',
      },
    },
    fetchPolicy: 'no-cache',
  });
  const allRawOrders = result.data?.orders?.orders || [];
  const excludedSet = new Set(data.excludedIds || []);
  return allRawOrders
    .map((o: any) => o.id as string | undefined)
    .filter((id: string | undefined): id is string => !!id && !excludedSet.has(id));
}

export const DownloadMultimediaPhotosModal = ({
  onClickClose,
  data,
  ...props
}: ModalProps): JSX.Element => {
  const { t } = useTranslation('modals');
  const theme = useTheme();
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [quality, setQuality] = useState<CloudinaryQuality>(
    CloudinaryQuality.Optimized,
  );

  const downloadPhotosAsZip = useCallback(async () => {
    setIsDownloading(true);

    try {
      // 1+2. Resolver imágenes: modo directo (selección del grid, ya resueltas)
      // o modo orden (listado: resolver orderIDs y obtener sus imágenes).
      const isDirect = !!data.directImages?.length;
      const imagesData = isDirect
        ? data.directImages!
        : await selectImagesByMultipleOrderIDs(await resolveOrderIds(data));

      if (imagesData.length === 0) {
        console.warn('No images to download');
        setIsDownloading(false);
        onClickClose();
        return;
      }

      setProgress({ current: 0, total: imagesData.length });

      // 3. Crear ZIP
      const zip = new JSZip();

      // 4. Descargar cada imagen y agregarla al ZIP
      for (let i = 0; i < imagesData.length; i++) {
        const { image, orderName } = imagesData[i];

        try {
          // La «alta» es el ORIGINAL de Hetzner (JPEG con su EXIF: GPS y fecha
          // de captura); la «optimizada», la versión de 1024 px de Cloudinary,
          // que también CONSERVA el EXIF (calidad fija + `fl_keep_iptc`, spec
          // fotos geolocalizadas § 17); las legacy siguen por Cloudinary. Si
          // la URL elegida falla, se baja el original (ver `fetchDownloadBlob`);
          // si no hay forma, lanza y el catch de abajo salta ESTA foto, como
          // antes.
          const blob = await fetchDownloadBlob(image, quality);

          // Obtener la extensión del blob
          const extension = getExtensionFromBlob(blob);

          // Modo directo (grid, una sola actuación): ZIP plano, ficheros en
          // la raíz. Modo orden (listado): carpeta por orden.
          const fileName = `${image.name}${extension}`;
          if (isDirect) {
            zip.file(fileName, blob);
          } else {
            zip.folder(`${orderName}`)?.file(fileName, blob);
          }

          setProgress({ current: i + 1, total: imagesData.length });
        } catch (error) {
          console.error(`Error downloading image ${image.name}:`, error);
          Sentry.captureException(error);
        }
      }

      // 5. Generar y descargar el ZIP con nombre descriptivo
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // Modo directo o una sola orden → ROID; múltiples → "multimedia" + fecha.
      let fileName: string;
      if (isDirect && imagesData.length > 0) {
        fileName = `${imagesData[0].orderName}.zip`;
      } else if (
        !data.selectAll &&
        (data.selectedRows?.length ?? 0) === 1 &&
        imagesData.length > 0
      ) {
        fileName = `${imagesData[0].orderName}.zip`;
      } else {
        fileName = `multimedia_${new Date().toISOString().split('T')[0]}.zip`;
      }
      await saveBlob(zipBlob, fileName);

      // Cerrar modal
      onClickClose();
    } catch (error) {
      console.error('Error creating ZIP:', error);
      Sentry.captureException(error);
    } finally {
      setIsDownloading(false);
    }
  }, [data, onClickClose, quality]);

  return (
    <ModalContainer
      width={45}
      onClickClose={onClickClose}
      fill={theme.colors.black}
    >
      <Container {...props}>
        <Icon src={DownloadIcon}></Icon>
        <Title size="XL" variant="semiBold">
          {t('downloadMultimediaPhotos.title')}
        </Title>
        {isDownloading ? (
          <>
            <Loader />
            <ProgressText size="M">
              {t('downloadMultimediaPhotos.progress', {
                current: progress.current,
                total: progress.total,
              })}
            </ProgressText>
          </>
        ) : (
          <>
            <CloudinaryQualitySelector
              value={quality}
              onChange={setQuality}
              disabled={isDownloading}
            />
            <Button
              onClick={downloadPhotosAsZip}
              text={t('downloadMultimediaPhotos.downloadButton')}
            ></Button>
          </>
        )}
      </Container>
    </ModalContainer>
  );
};
